import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { wyscoutMatchesAPI } from '@/services/api';
import { PlayerStats, Props } from './MatchPlayerStats/types';
import { COLUMNS, MAX_COL_WIDTH, MIN_COL_WIDTH } from './MatchPlayerStats/columns';
import { getPlayerTeamId } from './MatchPlayerStats/helpers';
import { LoadingPanel, ErrorPanel, EmptyPanel } from './MatchPlayerStats/StatusPanel';
import TeamToggle from './MatchPlayerStats/TeamToggle';
import PlayerStatsTable from './MatchPlayerStats/PlayerStatsTable';
import Legend from './MatchPlayerStats/Legend';

const MatchPlayerStats: React.FC<Props> = ({ matchId, homeTeamId, awayTeamId, homeTeamName, awayTeamName, playersData }) => {
  const controlled = playersData !== undefined;
  const [loading, setLoading] = useState(!controlled);
  const [error, setError] = useState<string | null>(null);
  const [fetchedPlayers, setFetchedPlayers] = useState<PlayerStats[]>([]);
  const [source, setSource] = useState<'database' | 'api' | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<'home' | 'away'>('home');
  const [sortBy, setSortBy] = useState<string>('minutesOnField');
  const [sortDesc, setSortDesc] = useState(true);
  const [playerMap, setPlayerMap] = useState<Record<number, string>>({});

  // Column resize state
  const [colWidths, setColWidths] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    COLUMNS.forEach(c => { initial[c.key] = c.defaultWidth; });
    return initial;
  });
  const dragRef = useRef<{ colKey: string; startX: number; startWidth: number } | null>(null);

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!dragRef.current) return;
    const { colKey, startX, startWidth } = dragRef.current;
    const newWidth = Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, startWidth + (e.clientX - startX)));
    setColWidths(prev => ({ ...prev, [colKey]: newWidth }));
  }, []);

  const onMouseUp = useCallback(() => {
    dragRef.current = null;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [onMouseMove]);

  const startResize = useCallback((colKey: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { colKey, startX: e.clientX, startWidth: colWidths[colKey] };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [colWidths, onMouseMove, onMouseUp]);

  const resizeColumn = useCallback((colKey: string, delta: number) => {
    setColWidths(previous => ({
      ...previous,
      [colKey]: Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, previous[colKey] + delta)),
    }));
  }, []);

  // Cleanup listeners on unmount in case drag is in progress
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [onMouseMove, onMouseUp]);

  useEffect(() => {
    if (controlled) return;

    let ignore = false;

    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await wyscoutMatchesAPI.getMatchPlayerStats(matchId);
        if (!ignore) {
          setFetchedPlayers(response.data || []);
          setSource(response.source);
        }
      } catch (err: unknown) {
        if (!ignore) {
          const message = isAxiosError<{ error?: string }>(err)
            ? err.response?.data?.error || err.message
            : err instanceof Error ? err.message : null;
          setError(message || 'Failed to load player stats');
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchStats();

    return () => {
      ignore = true;
    };
  }, [controlled, matchId]);

  const players = controlled ? playersData : fetchedPlayers;

  useEffect(() => {
    if (controlled) return;

    let ignore = false;

    const resolvePlayers = async () => {
      setPlayerMap({});

      if (players.length === 0) {
        return;
      }

      try {
        const resolvePayload = players.map(p => ({
          wyscoutPlayerId: p.playerId,
          name: p.name || '',
          shortName: p.shortName || '',
          position: p.position || '',
          currentTeamId: p.teamId || p.stats?.teamId || p.stats?.player?.currentTeamId || 0,
        }));
        const resolveResponse = await wyscoutMatchesAPI.resolvePlayers(resolvePayload);
        if (ignore) return;

        const map: Record<number, string> = {};
        for (const [wyscoutId, info] of Object.entries(resolveResponse.data)) {
          map[Number(wyscoutId)] = info.playerId;
        }
        setPlayerMap(map);
      } catch {
        if (!ignore) setPlayerMap({});
        // Non-critical - player links just won't work
      }
    };

    resolvePlayers();

    return () => {
      ignore = true;
    };
  }, [controlled, matchId, players]);

  const discoverTeamIds = (): { resolvedHomeId: number; resolvedAwayId: number } => {
    const playerTeamIds = new Set<number>();
    players.forEach(p => {
      const tid = getPlayerTeamId(p);
      if (tid) playerTeamIds.add(tid);
    });

    const uniqueIds = Array.from(playerTeamIds);

    // If match team IDs exist in player data, use them directly
    if (uniqueIds.includes(homeTeamId) && uniqueIds.includes(awayTeamId)) {
      return { resolvedHomeId: homeTeamId, resolvedAwayId: awayTeamId };
    }

    // If only one matches, the other must be the remaining one
    if (uniqueIds.length === 2) {
      if (uniqueIds.includes(homeTeamId)) {
        const other = uniqueIds.find(id => id !== homeTeamId)!;
        return { resolvedHomeId: homeTeamId, resolvedAwayId: other };
      }
      if (uniqueIds.includes(awayTeamId)) {
        const other = uniqueIds.find(id => id !== awayTeamId)!;
        return { resolvedHomeId: other, resolvedAwayId: awayTeamId };
      }
      return { resolvedHomeId: uniqueIds[0], resolvedAwayId: uniqueIds[1] };
    }

    // Fallback to match IDs
    return { resolvedHomeId: homeTeamId, resolvedAwayId: awayTeamId };
  };

  const { resolvedHomeId, resolvedAwayId } = discoverTeamIds();

  // Get players for a team, with fallback: any player NOT matching the other team
  // goes to this team. This handles cases where team IDs don't split cleanly.
  const getTeamPlayers = (teamId: number, otherTeamId: number) => {
    const directMatch = players.filter(p => getPlayerTeamId(p) === teamId);
    const otherMatch = players.filter(p => getPlayerTeamId(p) === otherTeamId);

    // If one side has all players and the other has none, redistribute
    if (directMatch.length === 0 && otherMatch.length === players.length) {
      // No players matched this team's ID - this team gets no players via direct match
      // Don't steal from the other team
      return [];
    }

    // If direct matching found a reasonable split, use it
    if (directMatch.length > 0) {
      return directMatch;
    }

    // Fallback: this team gets all players that DON'T belong to the other team
    return players.filter(p => getPlayerTeamId(p) !== otherTeamId);
  };

  const sortPlayers = (playerList: PlayerStats[]) => {
    return [...playerList].sort((a, b) => {
      const sortField = sortBy as keyof PlayerStats['stats']['total'];
      const aValue = a.stats?.total[sortField];
      const bValue = b.stats?.total[sortField];
      const aVal = typeof aValue === 'number' ? aValue : 0;
      const bVal = typeof bValue === 'number' ? bValue : 0;
      return sortDesc ? bVal - aVal : aVal - bVal;
    });
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDesc(!sortDesc);
    } else {
      setSortBy(field);
      setSortDesc(true);
    }
  };

  const tableWidth = Object.values(colWidths).reduce((a, b) => a + b, 0);

  if (!controlled && loading) {
    return <LoadingPanel />;
  }

  if (!controlled && error) {
    return <ErrorPanel error={error} />;
  }

  if (!players.length) {
    return <EmptyPanel />;
  }

  const homePlayers = sortPlayers(getTeamPlayers(resolvedHomeId, resolvedAwayId));
  const awayPlayers = sortPlayers(getTeamPlayers(resolvedAwayId, resolvedHomeId));
  const displayPlayers = selectedTeam === 'home' ? homePlayers : awayPlayers;
  const leaderMetrics = ([
    { label: 'xG', field: 'xgShot' },
    { label: 'Goals', field: 'goals' },
    { label: 'Key passes', field: 'keyPasses' },
    { label: 'Duels won', field: 'duelsWon' },
  ] as const).flatMap(({ label, field }) => {
    let leader: { player: PlayerStats; value: number } | undefined;

    displayPlayers.forEach(player => {
      const value = player.stats?.total[field];
      if (typeof value === 'number' && (!leader || value > leader.value)) {
        leader = { player, value };
      }
    });

    if (!leader || leader.value <= 0) return [];

    return [{
      label,
      name: leader.player.name || leader.player.shortName || 'Unknown player',
      value: field === 'xgShot' ? leader.value.toFixed(2) : leader.value.toLocaleString(),
    }];
  });

  return (
    <div className={`bg-white dark:bg-gray-900 rounded-lg shadow ${controlled ? 'p-4 sm:p-6' : 'p-6 mt-6'}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Player Statistics</h3>
        <div className="flex items-center gap-2">
          {!controlled && source && (
            <span className={`text-xs px-2 py-1 rounded ${source === 'database' ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'}`}>
              {players.length} players
            </span>
          )}
        </div>
      </div>

      {/* Team Toggle */}
      <TeamToggle
        selectedTeam={selectedTeam}
        onSelectTeam={setSelectedTeam}
        homeTeamName={homeTeamName}
        awayTeamName={awayTeamName}
        homeCount={homePlayers.length}
        awayCount={awayPlayers.length}
      />

      {leaderMetrics.length > 0 && (
        <section className="mb-4" aria-label={`${selectedTeam === 'home' ? homeTeamName || 'Home' : awayTeamName || 'Away'} team leaders`}>
          <h4 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Team leaders</h4>
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            {leaderMetrics.map(metric => (
              <div key={metric.label} className="min-w-0 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{metric.label}</span>
                  <span className="text-base font-semibold tabular-nums text-gray-900 dark:text-gray-100">{metric.value}</span>
                </div>
                <p className="truncate text-xs text-gray-600 dark:text-gray-300" title={metric.name}>{metric.name}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Stats Table */}
      <PlayerStatsTable
        displayPlayers={displayPlayers}
        playerMap={controlled ? {} : playerMap}
        colWidths={colWidths}
        tableWidth={tableWidth}
        sortBy={sortBy}
        sortDesc={sortDesc}
        handleSort={handleSort}
        startResize={startResize}
        resizeColumn={resizeColumn}
      />

      {/* Legend */}
      <Legend />
    </div>
  );
};

export default MatchPlayerStats;
